/**
 * Pixel Cache System - Phase 2: Pixel Optimization
 * 
 * Provides O(1) lookup of pre-computed pixel coordinates for 3D points
 * visible in the current camera. Eliminates expensive projection calculations.
 */

import { Camera, Point3DObservation, PointCameraObservation } from '../types/colmap'

export interface PixelCacheStats {
  hits: number
  misses: number
  buildTime: number
  cacheSize: number
  lastRebuild: number // timestamp
}

export class PixelCache {
  private cache: Map<number, [number, number]> = new Map()
  private stats: PixelCacheStats = {
    hits: 0,
    misses: 0,
    buildTime: 0,
    cacheSize: 0,
    lastRebuild: 0
  }

  /**
   * Get the underlying cache Map (for store integration)
   */
  getCache(): Map<number, [number, number]> {
    return this.cache
  }

  /**
   * Build pixel cache for current camera
   * Extracts point_id -> [x, y] mapping from camera's observed_points
   */
  buildFromCamera(camera: Camera): boolean {
    const startTime = performance.now()
    
    this.cache.clear()
    
    // Check if camera has pixel data (v2.0 format)
    const observedPoints = camera.observed_points
    if (!Array.isArray(observedPoints) || observedPoints.length === 0) {
      console.warn('[PixelCache] No observed points in camera', camera.id)
      return false
    }
    
    // Check format: array of numbers (v1.0) or objects with pixels (v2.0)
    const firstPoint = observedPoints[0]
    if (typeof firstPoint === 'number') {
      console.warn('[PixelCache] Camera has v1.0 format (IDs only), no pixel data available')
      return false
    }
    
    // Build cache from v2.0 format
    let added = 0
    let skipped = 0
    for (const obs of observedPoints as Point3DObservation[]) {
      // Validate observation structure and pixel data
      if (
        obs.point_id && 
        Array.isArray(obs.pixel) && 
        obs.pixel.length === 2 &&
        typeof obs.pixel[0] === 'number' &&
        typeof obs.pixel[1] === 'number' &&
        isFinite(obs.pixel[0]) &&
        isFinite(obs.pixel[1])
      ) {
        this.cache.set(obs.point_id, [obs.pixel[0], obs.pixel[1]])
        added++
      } else {
        skipped++
      }
    }
    
    if (skipped > 0) {
      console.warn(`[PixelCache] Skipped ${skipped} invalid observations out of ${observedPoints.length}`)
    }
    
    const buildTime = performance.now() - startTime
    
    this.stats = {
      hits: 0,
      misses: 0,
      buildTime,
      cacheSize: added,
      lastRebuild: Date.now()
    }
    
    console.log(`[PixelCache] Built cache for camera ${camera.id}: ${added} points in ${buildTime.toFixed(2)}ms`)
    return added > 0
  }

  /**
   * Build pixel cache from point_to_cameras mapping (alternative approach)
   * Uses the reverse lookup structure from v2.0 export
   */
  buildFromPointToCameras(
    pointToCameras: Record<number, PointCameraObservation[]>,
    currentCameraId: number
  ): boolean {
    const startTime = performance.now()
    
    this.cache.clear()
    
    let added = 0
    for (const [pointIdStr, observations] of Object.entries(pointToCameras)) {
      const pointId = parseInt(pointIdStr, 10)
      
      // Find observation for current camera
      const obs = observations.find(o => o.camera_id === currentCameraId)
      if (obs && Array.isArray(obs.pixel) && obs.pixel.length === 2) {
        this.cache.set(pointId, [obs.pixel[0], obs.pixel[1]])
        added++
      }
    }
    
    const buildTime = performance.now() - startTime
    
    this.stats = {
      hits: 0,
      misses: 0,
      buildTime,
      cacheSize: added,
      lastRebuild: Date.now()
    }
    
    console.log(`[PixelCache] Built cache from point_to_cameras for camera ${currentCameraId}: ${added} points in ${buildTime.toFixed(2)}ms`)
    return added > 0
  }

  /**
   * Get pixel coordinates for a 3D point
   * Returns [x, y] if point is visible in current camera, null otherwise
   */
  getPixel(pointId: number): [number, number] | null {
    const pixel = this.cache.get(pointId)
    
    if (pixel) {
      this.stats.hits++
      return pixel
    } else {
      this.stats.misses++
      return null
    }
  }

  /**
   * Check if cache has pixel data for a point
   */
  has(pointId: number): boolean {
    return this.cache.has(pointId)
  }

  /**
   * Get cache statistics
   */
  getStats(): PixelCacheStats {
    return { ...this.stats }
  }

  /**
   * Get cache hit rate (0.0 to 1.0)
   */
  getHitRate(): number {
    const total = this.stats.hits + this.stats.misses
    return total > 0 ? this.stats.hits / total : 0
  }

  /**
   * Clear cache and reset statistics
   */
  clear(): void {
    this.cache.clear()
    this.stats = {
      hits: 0,
      misses: 0,
      buildTime: 0,
      cacheSize: 0,
      lastRebuild: 0
    }
  }

  /**
   * Get cache size (number of cached points)
   */
  size(): number {
    return this.cache.size
  }
}

/**
 * Create and initialize pixel cache for a camera
 */
export function createPixelCache(camera: Camera): PixelCache | null {
  const cache = new PixelCache()
  const success = cache.buildFromCamera(camera)
  return success ? cache : null
}
